import { HelpText, RenderIf } from 'react-rainbow-components';
import { TrendingDown, TrendingUp } from 'lucide-react';
import formatMetricValue from '../../helpers/formatMetricValue';
import getMetricInfo from '../../helpers/getMetricInfo';
import { GlobalMetricValueFormat, HealthScoreMetricLabels, MetricTrendSnapshot } from '../../types';
import {
    DrawerMetricCardChange,
    DrawerMetricCardChangeIcon,
    DrawerMetricCardHeader,
    DrawerMetricCardHelpWrap,
    DrawerMetricCardRoot,
    DrawerMetricCardTile,
    DrawerMetricCardTitle,
    DrawerMetricCardValue,
    DrawerMetricCardValueRow,
} from './styled';

export interface DrawerMetricCardProps {
    metricKey: string;
    row: MetricTrendSnapshot;
    labels?: HealthScoreMetricLabels;
    format?: GlobalMetricValueFormat;
    /** Use tile layout inside channel drawer grid. */
    variant?: 'stack' | 'tile';
}

const DrawerMetricCard = ({
    metricKey,
    row,
    format: formatProp,
    variant = 'stack',
    labels,
}: DrawerMetricCardProps) => {
    const info = getMetricInfo(metricKey);
    const title = labels?.label ?? labels?.label_short ?? info?.label ?? metricKey;
    const description = labels?.description ?? info?.description ?? '';
    const valueFormat: GlobalMetricValueFormat = formatProp ?? info?.format ?? 'none';
    const { change = 0 } = row;
    const showChangeTrend = Number.isFinite(change) && change !== 0;
    const changePositive = change > 0;
    const goodWhen = info?.goodWhen ?? 'positive';

    const Root = variant === 'tile' ? DrawerMetricCardTile : DrawerMetricCardRoot;

    return (
        <Root>
            <DrawerMetricCardHeader>
                <DrawerMetricCardTitle>{title}</DrawerMetricCardTitle>
                {description ? (
                    <DrawerMetricCardHelpWrap>
                        <HelpText text={description} iconSize="small" />
                    </DrawerMetricCardHelpWrap>
                ) : null}
            </DrawerMetricCardHeader>
            <DrawerMetricCardValueRow>
                <DrawerMetricCardValue>
                    {formatMetricValue(row.current, valueFormat)}
                </DrawerMetricCardValue>
                <RenderIf isTrue={showChangeTrend}>
                    <DrawerMetricCardChange
                        $positive={changePositive}
                        $goodWhen={goodWhen}
                    >
                        <DrawerMetricCardChangeIcon>
                            {changePositive ? <TrendingUp /> : <TrendingDown />}
                        </DrawerMetricCardChangeIcon>
                        {`${changePositive ? '+' : '-'}${formatMetricValue(Math.abs(change), valueFormat)}`}
                    </DrawerMetricCardChange>
                </RenderIf>
            </DrawerMetricCardValueRow>
        </Root>
    );
};

export default DrawerMetricCard;
